import React, { useMemo } from 'react';
import { range } from 'lodash';
import { useSearchParams } from 'react-router-dom';
import clsx from 'clsx';

import Typography from 'components/ui/Typography';

import css from './Pagination.module.scss';

interface Props {
    total: number;
    pageSize: number;
    setPage: (page: number) => void;
    title?: string;
    siblingCount?: number;
}

const Pagination: React.FC<Props> = ({ total, pageSize, setPage, title, siblingCount = 1 }) => {
    const [searchParams] = useSearchParams();

    const ellipsis = useMemo(() => <div className={css.item}>...</div>, []);

    const currentPage = Number(searchParams.get('page')) === 0 ? 1 : Number(searchParams.get('page'));

    const pages = useMemo(() => {
        const totalPageCount = Math.ceil(total / pageSize);
        const totalPageNumbers = siblingCount + 5;

        if (totalPageNumbers >= totalPageCount) {
            return range(1, totalPageCount === 1 ? 2 : totalPageCount);
        }

        const leftSiblingIndex = Math.max(currentPage - siblingCount, 1);
        const rightSiblingIndex = Math.min(currentPage + siblingCount, totalPageCount);

        const shouldShowLeftDots = leftSiblingIndex > 2;
        const shouldShowRightDots = rightSiblingIndex < totalPageCount - 2;

        const firstPageIndex = 1;
        const lastPageIndex = totalPageCount;

        if (!shouldShowLeftDots && shouldShowRightDots) {
            const leftItemCount = 4 + 2 * siblingCount;
            const leftRange = range(1, leftItemCount);

            return [...leftRange, ellipsis, totalPageCount];
        }

        if (shouldShowLeftDots && !shouldShowRightDots) {
            const rightItemCount = 4 + 2 * siblingCount;
            const rightRange = range(totalPageCount - rightItemCount + 2, totalPageCount + 1);

            return [firstPageIndex, ellipsis, ...rightRange];
        }

        if (shouldShowLeftDots && shouldShowRightDots) {
            const middleRange = range(leftSiblingIndex, rightSiblingIndex + 1);

            return [firstPageIndex, ellipsis, ...middleRange, ellipsis, lastPageIndex];
        }
    }, [total, pageSize, currentPage, siblingCount]);

    const isEllipsis = pages?.some((page) => typeof page !== 'number');

    return (
        <div className={css.pagesContainer}>
            {title && (
                <Typography style="BodyMedSemibold" className={css.title}>
                    {title}
                </Typography>
            )}
            <div className={css.pages}>
                {pages?.map((page, idx) => {
                    const isActive = currentPage === page;

                    return (
                        <div
                            key={idx}
                            className={clsx(css.item, isActive && css['isActive'], !isEllipsis && css.maxWidth)}
                            onClick={() => typeof page === 'number' && setPage(page)}
                        >
                            {page}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default React.memo(Pagination);
